import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react'
import type { SiteDocument } from '../api/documents'
import type { Project } from '../content/projects.seed'
import type { Locale } from '../content/site'
import type { DiscordPresence } from '../lib/discord'
import { projectCount } from '../lib/plural'
import {
  boot,
  complete,
  interpret,
  line,
  longestCommonPrefix,
  plain,
  promptSpans,
  span,
  t,
  text,
  USER,
  type Env,
  type Exec,
  type GuiState,
  type Line,
  type ShellState,
  type Span,
} from '../lib/terminal'
import {
  applyTheme,
  defaultTheme,
  isPresetName,
  presets,
  savedTheme,
  storeTheme,
  type Theme,
} from '../lib/theme'
import { DiscordStatus } from './DiscordStatus'
import { ThemePanel } from './ThemePanel'

/* The GUI app loads on demand: the fake X11 boot stream hides the fetch. */
const XWindow = lazy(() => import('./xapp/XWindow').then((m) => ({ default: m.XWindow })))
const PortfolioApp = lazy(() =>
  import('./xapp/PortfolioApp').then((m) => ({ default: m.PortfolioApp })),
)

const HOME = `/home/${USER}`
const HOST = 'cadillac'
const HISTORY_KEY = 'mk69-history-v1'
const SCROLLBACK_LIMIT = 1500
const HISTORY_LIMIT = 200

/* ---------------------------------------------------------------- helpers */

const loadHistory = (): string[] => {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]')
    return Array.isArray(saved)
      ? saved.filter((item): item is string => typeof item === 'string')
      : []
  } catch {
    return []
  }
}

const storeHistory = (history: string[]): void => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-HISTORY_LIMIT)))
  } catch {
    // Storage may be unavailable; history then only lasts the session.
  }
}

const reducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const envFor = (locale: Locale): Env => ({
  USER,
  HOST,
  HOME,
  SHELL: '/usr/bin/zsh',
  TERM: 'xterm-256color',
  LANG: locale === 'ru' ? 'ru_RU.UTF-8' : 'en_US.UTF-8',
  EDITOR: 'nvim',
})

/* -------------------------------------------------------------- scrollback */

type Entry = { id: number; line: Line }
type Scrollback = { entries: Entry[]; nextId: number }
type ScrollbackAction = { type: 'push'; lines: Line[] } | { type: 'clear' }

/**
 * A reducer rather than `useState` + a ref counter: ids must be allocated
 * atomically with the append, and the scrollback must be trimmed in the same
 * step so React never sees an unbounded list.
 */
function scrollbackReducer(state: Scrollback, action: ScrollbackAction): Scrollback {
  if (action.type === 'clear') return { entries: [], nextId: state.nextId }
  if (!action.lines.length) return state
  const appended = action.lines.map((line, index) => ({ id: state.nextId + index, line }))
  return {
    entries: [...state.entries, ...appended].slice(-SCROLLBACK_LIMIT),
    nextId: state.nextId + action.lines.length,
  }
}

/* --------------------------------------------------------------- rendering */

function OutputSpan({ item }: { item: Span }) {
  if (item.href) {
    return (
      <a className="s-link" href={item.href} target="_blank" rel="noreferrer">
        {item.text}
      </a>
    )
  }
  return item.style ? <span className={`s-${item.style}`}>{item.text}</span> : <>{item.text}</>
}

/**
 * Memoised: scrollback lines are immutable once written, so typing a character
 * must not re-render thousands of rows.
 */
const OutputLine = memo(function OutputLine({ line }: { line: Line }) {
  return (
    <div className={line.art ? 'terminal-line terminal-line--art' : 'terminal-line'}>
      {line.spans.map((item, index) => (
        <OutputSpan key={index} item={item} />
      ))}
      {line.media && (
        <figure className="terminal-media">
          <img src={line.media.src} alt={line.media.alt} loading="lazy" />
          <figcaption aria-hidden="true">{line.media.alt}</figcaption>
        </figure>
      )}
    </div>
  )
})

/* --------------------------------------------------------------- component */

export function Terminal({
  locale,
  projects,
  documents,
  loading,
  failed,
  discord,
  onLanguage,
}: {
  locale: Locale
  projects: Project[]
  documents: SiteDocument[]
  loading: boolean
  failed: boolean
  discord: DiscordPresence
  onLanguage: (locale: Locale) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)
  const streamRef = useRef<number | undefined>(undefined)
  /** Pending window map, so the launch lines finish streaming first. */
  const guiTimerRef = useRef<number | undefined>(undefined)
  /** Guards the boot reveal so it runs once per real mount. */
  const busyOnMount = useRef(!reducedMotion())

  const [bootedAt] = useState(() => Date.now())
  /** Computed once, from the locale the session opened with. */
  const [bootLines] = useState(() =>
    boot({
      cwd: HOME,
      locale,
      env: envFor(locale),
      status: 0,
      history: [],
      projects: [],
      documents: [],
      loading: true,
      failed: false,
      discord: { state: 'loading' },
      gui: 'closed',
      now: () => new Date(),
      uptime: () => 0,
    }),
  )
  /* Reduced motion skips the reveal entirely: the banner is there on frame 1. */
  const [scrollback, dispatch] = useReducer(scrollbackReducer, null, () =>
    reducedMotion()
      ? { entries: bootLines.map((line, id) => ({ id, line })), nextId: bootLines.length }
      : { entries: [], nextId: 0 },
  )
  const [input, setInput] = useState('')
  const [caret, setCaret] = useState(0)
  const [focused, setFocused] = useState(true)
  const [busy, setBusy] = useState(() => !reducedMotion())
  const [cwd, setCwd] = useState(HOME)
  const [status, setStatus] = useState(0)
  const [history, setHistory] = useState<string[]>(loadHistory)
  const [historyCursor, setHistoryCursor] = useState(-1)
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState<{ query: string; index: number } | null>(null)
  const [theme, setTheme] = useState<Theme>(savedTheme)
  const [themeOpen, setThemeOpen] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [gui, setGui] = useState<GuiState>('closed')

  const prompt = promptSpans({ cwd, status })

  /** Built per call from the current closure — never stored in a ref. */
  const shellState = (): ShellState => ({
    cwd,
    locale,
    env: envFor(locale),
    status,
    history,
    projects,
    documents,
    loading,
    failed,
    discord,
    gui,
    now: () => new Date(),
    uptime: () => (Date.now() - bootedAt) / 1000,
  })

  const push = useCallback((lines: Line[]) => dispatch({ type: 'push', lines }), [])

  /* ------------------------------------------------------------- streaming */

  const stopStream = useCallback(() => {
    if (streamRef.current !== undefined) {
      window.clearInterval(streamRef.current)
      streamRef.current = undefined
    }
    setBusy(false)
  }, [])

  const stream = useCallback(
    (lines: Line[], interval: number) => {
      if (reducedMotion() || interval <= 0) {
        push(lines)
        return
      }
      setBusy(true)
      const queue = [...lines]
      streamRef.current = window.setInterval(() => {
        const line = queue.shift()
        if (line) push([line])
        if (!queue.length) stopStream()
      }, interval)
    },
    [push, stopStream],
  )

  /** Reveal everything still queued, e.g. when the reader hits a key. */
  const flushStream = useCallback(() => {
    if (streamRef.current === undefined) return false
    stopStream()
    return true
  }, [stopStream])

  useEffect(() => stopStream, [stopStream])

  useEffect(
    () => () => {
      if (guiTimerRef.current !== undefined) window.clearTimeout(guiTimerRef.current)
    },
    [],
  )

  /* ------------------------------------------------------------------ boot */

  useEffect(() => {
    if (!busyOnMount.current) return
    busyOnMount.current = false

    // Only the interval callback touches state — calling setState in the effect
    // body would cascade a render before the first paint. The cleanup resets so
    // StrictMode's remount replays the banner instead of losing it.
    let index = 0
    const timer = window.setInterval(() => {
      dispatch({ type: 'push', lines: [bootLines[index]] })
      index += 1
      if (index >= bootLines.length) {
        window.clearInterval(timer)
        streamRef.current = undefined
        setBusy(false)
      }
    }, 12)
    streamRef.current = timer

    return () => {
      window.clearInterval(timer)
      if (index < bootLines.length) {
        busyOnMount.current = true
        dispatch({ type: 'clear' })
      }
    }
  }, [bootLines])

  /* ----------------------------------------------------------------- theme */

  useEffect(() => {
    applyTheme(theme)
    storeTheme(theme)
  }, [theme])

  /* ------------------------------------------------------- scroll and title */

  useEffect(() => {
    const onScroll = () => {
      const root = document.documentElement
      stickToBottomRef.current = root.scrollHeight - root.scrollTop - root.clientHeight < 160
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    // Never yank the viewport away from someone reading further up.
    if (stickToBottomRef.current) endRef.current?.scrollIntoView({ block: 'end' })
  }, [scrollback, busy])

  useEffect(() => {
    const folder = cwd === HOME ? '~' : cwd.split('/').filter(Boolean).at(-1) ?? '/'
    document.title = `${USER}@${HOST}: ${folder} — mk69.dev`
  }, [cwd])

  /* ----------------------------------------------------------------- shell */

  const echoLine = (value: string): Line => ({ spans: [...prompt, span(' '), span(value)] })

  const applyResult = (value: string, result: Exec) => {
    setStatus(result.status)
    if (result.clear) dispatch({ type: 'clear' })
    if (result.historyClear) {
      setHistory([])
      storeHistory([])
    }
    if (result.cwd) setCwd(result.cwd)
    if (result.locale) onLanguage(result.locale)
    if (result.theme) {
      setTheme((old) =>
        result.theme === 'reset'
          ? defaultTheme
          : result.theme && isPresetName(result.theme)
            ? presets[result.theme]
            : old,
      )
    }
    if (result.open) window.open(result.open, '_blank', 'noopener,noreferrer')
    if (result.gui === 'resume') setGui('open')
    if (result.gui === 'open') {
      // Map the window only after the fake X11 boot lines have streamed out.
      const delay =
        result.stream && !reducedMotion() ? result.lines.length * result.stream + 400 : 250
      guiTimerRef.current = window.setTimeout(() => {
        guiTimerRef.current = undefined
        setGui('open')
      }, delay)
    }
    if (result.stream) stream(result.lines, result.stream)
    else push(result.lines)

    setAnnouncement(
      result.lines.length
        ? `${value}: ${result.lines.slice(0, 3).map(plain).join('. ')}${
            result.lines.length > 3 ? ` … ${result.lines.length} lines total` : ''
          }`
        : `${value}: done`,
    )
  }

  const run = (value: string) => {
    const trimmed = value.trim()
    push([echoLine(value)])
    setInput('')
    setCaret(0)
    setHistoryCursor(-1)
    setDraft('')
    if (!trimmed) return

    const nextHistory =
      history.at(-1) === trimmed ? history : [...history, trimmed].slice(-HISTORY_LIMIT)
    setHistory(nextHistory)
    storeHistory(nextHistory)
    applyResult(trimmed, interpret(trimmed, shellState()))
  }

  const submit = (event?: FormEvent) => {
    event?.preventDefault()
    if (busy) return
    if (search) {
      const match = searchMatch()
      setSearch(null)
      run(match ?? input)
      return
    }
    run(input)
  }

  /* ---------------------------------------------------------------- search */

  const searchMatches = (query: string): string[] =>
    query ? [...history].reverse().filter((item) => item.includes(query)) : []

  const searchMatch = (): string | undefined =>
    search ? searchMatches(search.query)[search.index] : undefined

  const exitSearch = (keep = true) => {
    if (!search) return
    const match = searchMatch()
    setSearch(null)
    if (keep && match) {
      setInput(match)
      setCaret(match.length)
    }
  }

  /* -------------------------------------------------------------- keyboard */

  const setInputAndCaret = (value: string, position = value.length) => {
    setInput(value)
    setCaret(position)
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(position, position))
  }

  const historyStep = (direction: 1 | -1) => {
    const next =
      direction === 1
        ? Math.min(historyCursor + 1, history.length - 1)
        : Math.max(historyCursor - 1, -1)
    if (next === historyCursor) return
    if (historyCursor === -1 && next >= 0) setDraft(input)
    setHistoryCursor(next)
    setInputAndCaret(next < 0 ? draft : history[history.length - 1 - next] ?? '')
  }

  const completeInput = () => {
    const { candidates, start } = complete(input, shellState())
    if (!candidates.length) return
    const head = input.slice(0, start)
    if (candidates.length === 1) {
      setInputAndCaret(`${head}${candidates[0]}${candidates[0].endsWith('/') ? '' : ' '}`)
      return
    }
    const prefix = longestCommonPrefix(candidates)
    if (prefix.length > input.length - start) {
      setInputAndCaret(`${head}${prefix}`)
      return
    }
    push([echoLine(input), text(candidates.join('  '), 'dim')])
  }

  const deleteWordBefore = (target: HTMLInputElement) => {
    const position = target.selectionStart ?? input.length
    const head = input.slice(0, position).replace(/\S+\s*$/, '')
    setInputAndCaret(head + input.slice(position), head.length)
  }

  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const key = event.key
    const lower = key.length === 1 ? key.toLowerCase() : key
    const target = event.currentTarget

    /* Reverse-i-search swallows most keys, exactly as readline does. */
    if (search) {
      if (key === 'Escape' || (event.ctrlKey && lower === 'g')) {
        event.preventDefault()
        exitSearch(false)
        return
      }
      if (event.ctrlKey && lower === 'r') {
        event.preventDefault()
        setSearch((old) =>
          old
            ? {
                ...old,
                index: Math.min(old.index + 1, Math.max(0, searchMatches(old.query).length - 1)),
              }
            : old,
        )
        return
      }
      if (key === 'Backspace') {
        event.preventDefault()
        setSearch((old) => (old ? { query: old.query.slice(0, -1), index: 0 } : old))
        return
      }
      if (key === 'Enter') return // the form submit runs the match
      if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault()
        setSearch((old) => (old ? { query: old.query + key, index: 0 } : old))
        return
      }
      if (key === 'Tab' && event.shiftKey) return
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(key)) {
        event.preventDefault()
        exitSearch()
      }
      return
    }

    if (busy) {
      if (event.ctrlKey && lower === 'c') {
        event.preventDefault()
        stopStream()
        // ^C during the fake X11 boot also cancels the pending window map.
        if (guiTimerRef.current !== undefined) {
          window.clearTimeout(guiTimerRef.current)
          guiTimerRef.current = undefined
        }
        push([text('^C', 'dim')])
        setStatus(130)
        return
      }
      // Any other key skips the animation instead of being swallowed.
      if (key !== 'Tab' && !event.ctrlKey && !event.metaKey && flushStream()) event.preventDefault()
      return
    }

    if (event.ctrlKey && !event.altKey && !event.metaKey) {
      switch (lower) {
        case 'r':
          event.preventDefault()
          setSearch({ query: '', index: 0 })
          return
        case 'l':
          event.preventDefault()
          dispatch({ type: 'clear' })
          return
        case 'c': {
          const hasSelection =
            target.selectionStart !== target.selectionEnd ||
            Boolean(window.getSelection()?.toString())
          if (hasSelection) return // let the browser copy
          event.preventDefault()
          push([{ spans: [...prompt, span(' '), span(input), span('^C', 'dim')] }])
          setInputAndCaret('')
          setHistoryCursor(-1)
          setStatus(130)
          return
        }
        case 'd':
          if (!input) {
            event.preventDefault()
            run('exit')
          }
          return
        case 'a':
          event.preventDefault()
          target.setSelectionRange(0, 0)
          setCaret(0)
          return
        case 'e':
          event.preventDefault()
          target.setSelectionRange(input.length, input.length)
          setCaret(input.length)
          return
        case 'u': {
          event.preventDefault()
          const position = target.selectionStart ?? input.length
          setInputAndCaret(input.slice(position), 0)
          return
        }
        case 'k': {
          event.preventDefault()
          const position = target.selectionStart ?? input.length
          setInputAndCaret(input.slice(0, position), position)
          return
        }
        case 'w':
          event.preventDefault()
          deleteWordBefore(target)
          return
        default:
          return
      }
    }

    if (event.altKey && key === 'Backspace') {
      event.preventDefault()
      deleteWordBefore(target)
      return
    }

    switch (key) {
      case 'ArrowUp':
        event.preventDefault()
        historyStep(1)
        return
      case 'ArrowDown':
        event.preventDefault()
        historyStep(-1)
        return
      case 'Tab':
        // Shift+Tab must stay a focus move: intercepting it traps the keyboard
        // in the input and makes every link in the scrollback unreachable.
        if (event.shiftKey) return
        event.preventDefault()
        completeInput()
        return
      case 'Escape':
        setInputAndCaret('')
        setHistoryCursor(-1)
        return
    }
  }

  /* ------------------------------------------------------------------- gui */

  const closeGui = useCallback(() => {
    setGui('closed')
    push([line(span('[portfolio] ', 'accent'), span(t('guiExited', locale), 'dim'))])
    inputRef.current?.focus()
  }, [push, locale])

  const suspendGui = useCallback(() => {
    setGui('suspended')
    push([
      line(span('zsh: suspended  ', 'warn'), span('portfolio', 'exec')),
      text(t('guiSuspended', locale), 'dim'),
    ])
    inputRef.current?.focus()
  }, [push, locale])

  /* ---------------------------------------------------------------- render */

  const trackCaret = () => setCaret(inputRef.current?.selectionStart ?? 0)
  const match = searchMatch()

  return (
    <main
      className="terminal"
      id="content"
      onMouseUp={() => {
        // Clicks inside the GUI window must not yank focus back to the shell.
        if (gui === 'open') return
        if (!window.getSelection()?.toString()) inputRef.current?.focus()
      }}
    >
      <h1 className="sr-only">mk69.dev interactive shell</h1>

      <div className="terminal-toolbar">
        <DiscordStatus presence={discord} />
        <div className="theme-anchor">
          <button
            type="button"
            className="toolbar-button"
            aria-expanded={themeOpen}
            aria-haspopup="dialog"
            onMouseUp={(event) => event.stopPropagation()}
            onClick={() => setThemeOpen((open) => !open)}
          >
            {locale === 'ru' ? 'Тема' : 'Theme'}
          </button>
          {themeOpen && (
            <ThemePanel
              locale={locale}
              theme={theme}
              onTheme={setTheme}
              onClose={() => setThemeOpen(false)}
            />
          )}
        </div>
      </div>

      {/*
        Not an <output> element: its implicit `status` role makes the whole
        scrollback a live region, so every command re-announces the entire
        buffer. The dedicated summary at the end of this component announces
        instead.
      */}
      <div className="terminal-output" role="log" aria-live="off">
        {scrollback.entries.map((entry) => (
          <OutputLine key={entry.id} line={entry.line} />
        ))}
      </div>

      <form onSubmit={submit} className="terminal-form" hidden={busy}>
        {search ? (
          <label className="search-prompt" htmlFor="command">
            <span className="s-dim">(reverse-i-search)`</span>
            <span className="s-accent">{search.query}</span>
            <span className="s-dim">': </span>
            <span>{match ?? ''}</span>
          </label>
        ) : (
          <label className="prompt" htmlFor="command">
            {prompt.map((item, index) => (
              <OutputSpan key={index} item={item} />
            ))}
          </label>
        )}

        <div className={`term-input-wrap${search ? ' term-input-wrap--search' : ''}`}>
          <input
            ref={inputRef}
            id="command"
            className="term-input"
            value={input}
            maxLength={512}
            onChange={(event) => {
              setInput(event.target.value)
              setCaret(event.target.selectionStart ?? event.target.value.length)
            }}
            onKeyDown={keyDown}
            onKeyUp={trackCaret}
            onSelect={trackCaret}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            autoFocus
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            aria-label="Shell command"
            aria-describedby="terminal-help"
          />
          {!search && (
            <div className="term-mirror" aria-hidden="true">
              <span>{input.slice(0, caret)}</span>
              <span className={`caret${focused ? '' : ' caret--idle'}`}>{input[caret] ?? ' '}</span>
              <span>{input.slice(caret + 1)}</span>
            </div>
          )}
        </div>

        <span id="terminal-help" className="sr-only">
          Type help and press Enter for available commands. Tab completes, Ctrl+R searches history,
          Ctrl+L clears, Shift+Tab leaves the shell. Settings are stored locally.
        </span>
      </form>

      <div ref={endRef} />

      {gui !== 'closed' && (
        <Suspense fallback={null}>
          <XWindow
            locale={locale}
            suspended={gui === 'suspended'}
            statusLeft={`portfolio 1.1 · ${projectCount(projects.length, locale)}`}
            statusRight={`${USER}@${HOST} · :0`}
            onClose={closeGui}
            onMinimize={suspendGui}
          >
            <PortfolioApp projects={projects} locale={locale} />
          </XWindow>
        </Suspense>
      )}

      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>
    </main>
  )
}
